/**
 * Copyright © 2026 Adnuntius AS.
 */
import _ from 'lodash';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {MEDIA_TYPE, MediaTypeHelper} from '../../components/util/media-type-helper';
import {DEFAULT_LAYOUT_TYPES} from '../creative/creative-helper.service';
import {CreativeRetailProductModalService} from '../creative/creative-retail-product-modal.service';
import {ADN_TARGETING_TYPES} from '../targeting/targeting-constants';
import {ASPECT_RATIOS_TRANSLATIONS} from '../admin/layout/layout-commons';
import {resolveCreativePreviewAdvertiser} from '../creative/ad/preview-request-data';
import {requiresVideoPreviewParam} from '../creative/ad/preview-language';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {formatMediaTypesTranslate} from '../common/filters/format-media-type';
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {attachEditPagePhase} from '../common/util/edit-page-phase';
import type {EditPagePhaseApi} from '../common/util/edit-page-phase';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {findCreativeAdvertiserMismatch} from '../creative/creative-advertiser-mismatch';

export interface LibraryCreativeEditSetupParams {
  model: any;
  readOnly: boolean;
  creativeType: any;
  lineItemInfo: any;
  modelComms: any;
  navigationHistory: NavigationHistoryService;
  routeParams: any;
  translate: any;
  Team: any;
  ProductFeed: any;
  CreativeHelper: any;
  adnListHelper: AdnListHelperService;
  Rollbar: any;
  Asset: any;
  Creative: any;
  LibraryCreative: any;
  LocalNetworkProfile: any;
  LocalUserPermissions: any;
  Advertiser: any;
  TargetingEditorModalService: any;
  creativeRetailProductModalService: CreativeRetailProductModalService;
  cdr: any;
}

export function setupLibraryCreativeEditCtrl(ctrl: any, p: LibraryCreativeEditSetupParams): void {
  const {
    model, readOnly, lineItemInfo, modelComms, navigationHistory, routeParams, translate, Team, ProductFeed,
    CreativeHelper, adnListHelper, Rollbar, Asset, Creative, LibraryCreative,
    LocalNetworkProfile, LocalUserPermissions, Advertiser, TargetingEditorModalService,
    creativeRetailProductModalService, cdr
  } = p;

  const editPhase: EditPagePhaseApi = attachEditPagePhase(ctrl, cdr);
  const mInitPromises: any[] = [];
  const mLayoutAssetMatches: Record<string, any> = {};
  const defaultLayoutType = 'OTHER';
  const prevAssetAssignments: Record<string, any> = {};
  let mAllLayoutsPromise: any;
  let mPreviewAdvertiserPromise: any;
  let mPreviewAdvertiserId: any;
  let timeoutPromise: any;

  ctrl.editsMade = () => ctrl.markExternalFormDirty();
  ctrl.updateModelHappened = () => ctrl.publishModelUpdate();
  ctrl.isCreative = false;
  ctrl.skips = ['validation.warning.creative.blocked.by.scanner'];
  ctrl.isPureMarketplace = LocalNetworkProfile.hasAdvertiserEnforced();
  ctrl.aspectRatioDescriptions = ASPECT_RATIOS_TRANSLATIONS;
  ctrl.defaultLayoutTypes = DEFAULT_LAYOUT_TYPES;
  ctrl.layoutTypes = ctrl.defaultLayoutTypes.concat(defaultLayoutType);
  ctrl.perms = LocalUserPermissions.getAllVisPermissions();
  ctrl.linkedCreativeFields = ['name', {param: 'LineItem', label: 'lineItem'}, {label: 'dimensions', param: 'width'}, {param: '', label: 'info'}, 'updateTime', {param: '', label: 'targeting'}];
  ctrl.needsVerificationCheck = LocalNetworkProfile.isVerificationCheckNetwork();
  ctrl.retailProductFields = {
    retailProductId: true,
    merchantId: true,
    title: true,
    body: true,
    image: true,
    clickUrl: true,
    sponsoredBy: true,
    priceCurrency: true,
    priceAmount: true,
    salePriceCurrency: true,
    salePriceAmount: true
  };
  ctrl.layoutRef = {};

  const syncEditingModel = () => {
    ctrl.model = ctrl.editingModel;
  };

  const teamParams: any = {minimal: true};
  if (ctrl.isPureMarketplace) {
    teamParams.filterBy = 'type';
    teamParams.filterByValue = 'AX_ADVERTISER';
  }
  const teamQuery = Team.query(teamParams).$promise.then((page: any) => {
    // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
    // Resolve teamQuery only once ctrl.allTeams is actually set, since callers below
    // read ctrl.allTeams synchronously after this promise settles.
    return new Promise<void>((resolve) => {
      deferMarkForCheck(cdr, () => {
        ctrl.allTeams = page.results;
        resolve();
      });
    });
  });

  if (!ctrl.isNew) {
    adnListHelper.setUpBasicList(ctrl, Creative, 'fullCreativesforLibraryCreative', {
      moreParams: {libraryCreative: model.id},
      afterFill: () => {
        // Snapshot — do not bind ctrl.items (list-helper replaces that array
        // on settle and can race checkNoChanges).
        deferMarkForCheck(cdr, () => {
          ctrl.displayItems = (ctrl.items || []).slice();
          ctrl.hasItems = ctrl.displayItems.length > 0;
          ctrl.relCreativesEmpty = !ctrl.hasItems;
          ctrl.relCreatives = ctrl.displayItems;
          ctrl.relCreativesCount = ctrl.displayItems.length;
        });
      }
    });
  }

  const updateDefaultName = () => {
    CreativeHelper.updateDefaultName(ctrl);
  };

  const hydrateSelectedRetailProduct = (productId: string, merchantId: string) => {
    if (!productId || !merchantId) {
      ctrl.selRetailProduct = null;
      return Promise.resolve(null);
    }
    return ProductFeed.get({id: productId, merchantId: merchantId}).$promise.then((retailProd: any) => {
      ctrl.selRetailProduct = retailProd;
      ctrl.merchantGuide = ctrl.merchantGuide || {};
      ctrl.selRetailProduct.hydratedMerchant = ctrl.merchantGuide[ctrl.selRetailProduct.merchantId];
      return retailProd;
    });
  };

  const applyRetailCreativeUpdate = (creative: any) => {
    syncEditingModel();
    ctrl.editingModel = new LibraryCreative(creative);
    ctrl.model = ctrl.editingModel;
    const hydratedLayout = ctrl.layoutRef[_.get(ctrl.editingModel, ['layout', 'id'])];
    if (hydratedLayout) {
      ctrl.editingModel.layout = hydratedLayout;
    }
    ctrl.saveDirect(creative);
  };

  const ensureRetailCreativeIsSaved = () => {
    if (!ctrl.isNew) {
      return Promise.resolve(ctrl.editingModel);
    }
    ctrl.onBeforeSubmit();
    return ctrl.editingModel.savePartial().then((savedCreative: any) => {
      ctrl.editingModel.id = savedCreative.id;
      _.set(ctrl.editingModel, 'validationWarnings', savedCreative.validationWarnings);
      return savedCreative;
    });
  };

  const getCurrentPreviewAdvertiser = () => {
    const modelAdvertiserId = _.get(ctrl.editingModel, ['advertiser', 'id']);
    if (_.get(ctrl.advertiserInfo, 'id') && _.get(ctrl.advertiserInfo, 'id') === modelAdvertiserId) {
      return ctrl.advertiserInfo;
    }
    return ctrl.editingModel.advertiser || ctrl.advertiserInfo || null;
  };

  const getAdvertiserForPreview = () => {
    const advertiser = getCurrentPreviewAdvertiser();
    return Promise.resolve(resolveCreativePreviewAdvertiser(ctrl.editingModel, null, advertiser, (advertiserId: string) => {
      if (_.get(ctrl.advertiserInfo, 'id') === advertiserId && _.get(ctrl.advertiserInfo, 'legalName')) {
        return ctrl.advertiserInfo;
      }
      if (mPreviewAdvertiserPromise && mPreviewAdvertiserId === advertiserId) {
        return mPreviewAdvertiserPromise;
      }
      mPreviewAdvertiserId = advertiserId;
      mPreviewAdvertiserPromise = Advertiser.get({id: advertiserId}).$promise.then((data: any) => {
        ctrl.advertiserInfo = data;
        return data;
      }, () => advertiser);
      return mPreviewAdvertiserPromise;
    }));
  };

  const updateCreativeDims = () => {
    syncEditingModel();
    CreativeHelper.updateCreativeDims(ctrl, prevAssetAssignments);
  };

  const checkHtmlUrls = () => {
    syncEditingModel();
    CreativeHelper.checkHtmlUrls(ctrl);
  };

  const matchConstraintsAndAssets = (newAssetUpload?: boolean) => {
    syncEditingModel();
    return CreativeHelper.matchConstraintsAndAssets(ctrl, mInitPromises, mLayoutAssetMatches, prevAssetAssignments, delayedPreview, newAssetUpload);
  };

  const callForPreview = () => {
    Promise.all([Promise.resolve(mAllLayoutsPromise)]).then(() => getAdvertiserForPreview()).then((previewAdvertiser: any) => {
      syncEditingModel();
      const previewResults = (failureReason: string | null, previewData?: any) => {
        // Defer — selPreview flips [hidden]="!selPreview" true→false mid-CD (NG0100).
        deferMarkForCheck(cdr, () => {
          ctrl.selPreviewFail = failureReason;
          ctrl.selPreview = previewData;
          const selLayoutType = _.get(ctrl.editingModel, ['layout', 'type'], 'unspecified');
          const key = ctrl.defaultLayoutTypes.indexOf(selLayoutType) > -1 && selLayoutType !== 'HTML' ? 'style1' : 'style2';
          if (previewData && ctrl.selPreviewTrigger[key].previewCreative) {
            ctrl.selPreviewTrigger[key].previewCreative(previewData);
          }
        });
      };

      if (!ctrl.editingModel.layout || !ctrl.editingModel.layout.id) {
        previewResults('A valid layout needs to be selected before a creative can be previewed.');
        return;
      }

      const adServingWarnings = _.find(ctrl.editingModel.layout.validationWarnings, (v: any) => v.preventsAdServing);
      if (adServingWarnings) {
        previewResults('The select layout is invalid and cannot be used to preview a creative.');
        return;
      }

      const creativeAssets = ctrl.editingModel.assets.concat(ctrl.editingModel.libraryCreativeAssets || []);
      const availableAssets = _.map(creativeAssets, (asset: any) => _.omit(asset, ['matchingAcs', 'assetComponents']));
      if (availableAssets.length === 0 && _.filter(ctrl.editingModel.layout.assetComponents, ['required', 'MANDATORY']).length > 0) {
        previewResults('Mandatory assets must be made available before a creative can be previewed.');
        return;
      }

      const assets = _.filter(availableAssets, (asset: any) => {
        const assetIds = _.keyBy(_.valuesIn(ctrl.editingModel.cToAssets), 'id');
        return assetIds[asset.id];
      });
      const text = _.filter(ctrl.editingModel.cToText, (v: any) => !!v);
      const links = _.filter(ctrl.editingModel.cToUrls, (v: any) => !!v);
      const choices = _.filter(ctrl.editingModel.cToChoices, (v: any) => !!v);
      const mandatoryCompLength = _.filter(_.get(ctrl.editingModel, ['layout', 'layoutComponents'], []), (lc: any) => lc.required === 'MANDATORY').length;
      if (mandatoryCompLength > 0 && assets.length === 0 && text.length === 0 && links.length === 0 && choices.length === 0) {
        previewResults('At least one layout component needs an asset or text assigned to it before a creative can be previewed.');
        return;
      }

      if (ctrl.editingModel.dimensionType === 'FIXED' && (!ctrl.editingModel.width || !ctrl.editingModel.height)) {
        previewResults('The creative must have a width and height before it can be previewed.');
        return;
      }
      if (ctrl.editingModel.dimensionType === 'FIXED_HEIGHT' && !ctrl.editingModel.height) {
        previewResults('The creative must have a height before it can be previewed.');
        return;
      }
      if (ctrl.editingModel.dimensionType === 'FIXED_WIDTH' && !ctrl.editingModel.width) {
        previewResults('The creative must have a width before it can be previewed.');
        return;
      }

      const creativeModel = _.cloneDeep(ctrl.editingModel);
      CreativeHelper.doLayoutParams(ctrl, creativeModel);
      previewResults(null, {
        layoutId: ctrl.editingModel.layout.id,
        creative: creativeModel,
        lineItem: null,
        advertiser: previewAdvertiser,
        election: null,
        video: requiresVideoPreviewParam(ctrl.editingModel)
      });
    });
  };

  const delayedPreview = () => {
    clearTimeout(timeoutPromise);
    timeoutPromise = setTimeout(() => callForPreview(), 1500);
  };

  const overridableTargetingForCtrl = () => {
    ctrl.tOverrides = {};
    ctrl.tOverridesTypes = [];
    _.forEach(ADN_TARGETING_TYPES, (val: any) => {
      const ot = _.find(ctrl.editingModel.overridableTargeting, (tt: string) => tt === val.apiType);
      if (ot) {
        ctrl.tOverridesTypes.push(val.id);
      }
      ctrl.tOverrides[val.apiType] = !!ot;
    });
  };

  const checkShowGeneralVariables = () => {
    ctrl.showGeneralVariables = _.get(ctrl, ['editingModel', 'layout', 'layoutType']) === 'THIRD_PARTY' || !!_.find(ctrl.allComponents || [], (c: any) => c.type === 'TEXT' && c.textType === 'CODE');
  };

  const afterInitAfterSave = () => {
    syncEditingModel();
    // Save response replaces layout with a stub {id}; restore assetComponents etc. before CD.
    const layoutId = _.get(ctrl.editingModel, ['layout', 'id']);
    const layoutFromRef = layoutId && ctrl.layoutRef[layoutId];
    if (layoutFromRef) {
      ctrl.editingModel.layout = layoutFromRef;
    }
    ctrl.hasLibraryCreative = false;
    ctrl.lcOverrides = {};
    overridableTargetingForCtrl();
    ctrl.dimensionOverrides = false;
    ctrl.savedTeams = _.clone(ctrl.editingModel.teams);

    ctrl.advertiserMismatch = findCreativeAdvertiserMismatch(ctrl.editingModel.validationWarnings);

    Promise.resolve(mAllLayoutsPromise).then(() => {
      const hydratedLayout = ctrl.layoutRef[_.get(ctrl.editingModel, ['layout', 'id'])];
      if (hydratedLayout) {
        ctrl.editingModel.layout = hydratedLayout;
      }
      const productId = _.get(ctrl.editingModel, ['layoutParameters', 'retailProductId']);
      const merchantId = _.get(ctrl.editingModel, ['layoutParameters', 'merchantId']);
      if (hydratedLayout && hydratedLayout.detectedCategory === 'RETAIL' && productId && merchantId) {
        hydrateSelectedRetailProduct(productId, merchantId);
        const notRetailComponents = _.filter(ctrl.layoutRef[ctrl.editingModel.layout.id].layoutComponents, (lc: any) => !ctrl.retailProductFields[lc.tag]);
        ctrl.isPureRetailCreative = ctrl.layoutRef[ctrl.editingModel.layout.id].layoutType === 'RETAIL' && !notRetailComponents.length;
      }
    });

    if (ctrl.isPureMarketplace) {
      Promise.resolve(teamQuery).then(() => {
        ctrl.editingModel.singularTeam = ctrl.editingModel.teams[0];
        if (!ctrl.editingModel.singularTeam && ctrl.allTeams.length === 1) {
          ctrl.editingModel.singularTeam = _.get(ctrl, ['allTeams', 0, 'id']);
        }
      });
    }
    checkShowGeneralVariables();
  };

  const afterInit = () => {
    editPhase.wait('init');
    syncEditingModel();
    ctrl.editingModel = new LibraryCreative(ctrl.editingModel);
    ctrl.model = ctrl.editingModel;

    const axPublisherPermission = ctrl.editingModel.permission === 'AX_PUBLISHER';
    ctrl.readOnlyAxPublisher = axPublisherPermission && !ctrl.editingModel.targeting;
    ctrl.showTargeting = !axPublisherPermission || ctrl.editingModel.targeting;
    ctrl.blockSubmit = readOnly || ctrl.blockSubmit || ctrl.readOnlyAxPublisher;

    ctrl.editingModel.targeting = ctrl.editingModel.targeting || {};
    ctrl.editingModel.cToAssets = ctrl.editingModel.cToAssets || {};
    ctrl.editingModel.userState = ctrl.editingModel.userState || 'APPROVED';
    ctrl.editingModel.type = ctrl.editingModel.type || 'INTERNAL';
    ctrl.htmlAcWithAssetMatches = {};
    ctrl.selPreviewTrigger = {style1: {}, style2: {}};
    ctrl.editingModel.teams = _.map(ctrl.editingModel.teams, 'id');
    ctrl.editingModel.dimensionType = ctrl.editingModel.dimensionType || 'FIXED';
    ctrl.editingModel.renderOption = ctrl.editingModel.renderOption || null;

    const advertiser = advertiserFromNavigation(navigationHistory, routeParams);
    if (advertiser) {
      ctrl.editingModel.advertiser = advertiser;
      ctrl.advertiserSpecified = true;
    }

    if (!mAllLayoutsPromise) {
      mAllLayoutsPromise = CreativeHelper.getLayouts(ctrl, defaultLayoutType, null);
    }
    mInitPromises.push(mAllLayoutsPromise);

    if (ctrl.editingModel.id) {
      ctrl.models = [ctrl.editingModel];
      Promise.resolve(mAllLayoutsPromise).then(() => {
        if (ctrl.editingModel.layout) {
          const hydratedLayout = _.find(ctrl.layouts, ['id', ctrl.editingModel.layout.id]);
          if (hydratedLayout) {
            ctrl.editingModel.layout = hydratedLayout;
          }
        }
      });
    }

    Promise.all(mInitPromises).then(() => {
      return Promise.resolve(matchConstraintsAndAssets()).then(() => {
        delayedPreview();
        checkShowGeneralVariables();
      });
    }).then(() => {
      editPhase.done('init');
    }, () => {
      editPhase.done('init');
    });

    afterInitAfterSave();
  };

  const afterSave = (creative: any) => {
    syncEditingModel();
    ctrl.targetingResult = {data: false};
    ctrl.isNew = false;
    ctrl.editingModel.targeting = creative.targeting || {};
    ctrl.teamsWarning = (_.get(ctrl.editingModel, 'teams') || []).length !== (_.get(creative, 'teams') || []).length;
    _.set(ctrl.editingModel, 'teams', _.map(_.get(creative, 'teams') || [], 'id'));
    afterInitAfterSave();
    delayedPreview();
  };

  ctrl._libraryCreativeAfterInit = afterInit;
  ctrl._libraryCreativeAfterInitAfterSave = afterInitAfterSave;
  ctrl._libraryCreativeAfterSave = afterSave;

  ctrl.lineItemInfo = lineItemInfo || {};

  ctrl.assetAssignmentChanged = (tag: string) => {
    syncEditingModel();
    const selectedAsset = _.get(ctrl.editingModel, 'cToAssets.' + tag);
    if (selectedAsset && MediaTypeHelper.isMediaType(selectedAsset.mimeType, MEDIA_TYPE.html) && ctrl.primaryHtmlAsset) {
      ctrl.primaryHtmlAsset = selectedAsset;
      checkHtmlUrls();
    }
    updateCreativeDims();
    delayedPreview();
    updateDefaultName();
    if (_.isFunction(ctrl.eventHook.onAssetUpdate)) {
      ctrl.eventHook.onAssetUpdate();
    }
  };

  ctrl.selectTeams = () => {
    ctrl.editsMade();
    ctrl.editingModel.teams = _.map(ctrl.allTeams, 'id');
  };

  ctrl.deselectTeams = () => {
    ctrl.editsMade();
    ctrl.editingModel.teams = [];
  };

  ctrl.isTeamSelected = (teamId: string) => (ctrl.editingModel.teams || []).indexOf(teamId) > -1;

  ctrl.toggleTeam = (teamId: string) => {
    ctrl.editsMade();
    const teams = ctrl.editingModel.teams || [];
    const idx = teams.indexOf(teamId);
    if (idx > -1) {
      teams.splice(idx, 1);
    } else {
      teams.push(teamId);
    }
    ctrl.editingModel.teams = teams;
  };

  ctrl.selectLayout = (layout?: any) => {
    syncEditingModel();
    if (layout) {
      ctrl.editingModel.layout = layout;
    }
    ctrl.editingModel.cToAssets = {};
    // ngModelChange can fire with null when the layout select is cleared.
    if (!ctrl.editingModel.layout) {
      ctrl.editingModel.defaultLayoutWidth = null;
      ctrl.editingModel.defaultLayoutHeight = null;
      return;
    }
    if (ctrl.editingModel.defaultLayoutWidth === ctrl.editingModel.width && ctrl.editingModel.defaultLayoutHeight === ctrl.editingModel.height) {
      ctrl.editingModel.width = ctrl.editingModel.layout.defaultWidth;
      ctrl.editingModel.height = ctrl.editingModel.layout.defaultHeight;
    }
    ctrl.editingModel.width = ctrl.editingModel.width || ctrl.editingModel.layout.defaultWidth;
    ctrl.editingModel.height = ctrl.editingModel.height || ctrl.editingModel.layout.defaultHeight;
    ctrl.editingModel.defaultLayoutWidth = ctrl.editingModel.layout.defaultWidth;
    ctrl.editingModel.defaultLayoutHeight = ctrl.editingModel.layout.defaultHeight;
    Promise.resolve(matchConstraintsAndAssets(true)).then(() => {
      checkShowGeneralVariables();
      callForPreview();
    });
  };

  ctrl.eventHook = {
    onUpdate: () => {
      ctrl.editsMade();
      matchConstraintsAndAssets(true);
    },
    assignAsset: (asset: any, tag: string) => {
      ctrl.editsMade();
      ctrl.editingModel.cToAssets[tag] = asset;
      delayedPreview();
    }
  };

  ctrl.componentValueUpdated = () => {
    ctrl.editsMade();
    delayedPreview();
  };

  ctrl.dimensionsChange = () => {
    delayedPreview();
  };

  ctrl.editTarget = () => {
    ctrl.targetingResult = {data: false};
    const modelLineItem: any = {creativesForReach: [ctrl.editingModel], fromCreative: true};
    TargetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: modelLineItem,
      pageType: 'creative',
      reachEnabled: 'CREATIVE',
      targetingMode: null,
      overridableTargeting: null,
      mandatoryTargets: undefined,
      availableTargets: undefined,
      targeting: _.cloneDeep(ctrl.editingModel.targeting),
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(ctrl.editingModel.targeting, wc.wc);
      ctrl.editingModel.targeting = _.cloneDeep(ctrl.editingModel.targeting);
      if (!ctrl.isNew) {
        ctrl.editingModel.savePartial(['targeting']).then((respCreative: any) => {
          ctrl.targetingResult.data = true;
          _.set(ctrl.editingModel, 'validationWarnings', respCreative.validationWarnings);
          _.set(ctrl.editingModel, 'targeting', respCreative.targeting);
        }, (err: any) => {
          ctrl.targetingResult.errors = _.get(err, ['data', 'errors']);
          Rollbar.error('Library creative targeting problem', err);
        });
      } else {
        ctrl.editsMade();
      }
    }, () => undefined);
  };

  ctrl.refreshCodeMirrorVar = false;
  ctrl.refreshCodeMirror = () => {
    setTimeout(() => {
      ctrl.refreshCodeMirrorVar = true;
    }, 100);
  };

  modelComms.addSubscriber((data: any, type: any) => {
    if (data && type === OBJECT_TYPE.Advertiser) {
      ctrl.advertiserInfo = data;
    }
  });

  ctrl._libraryCreativeOnBeforeSubmit = () => {
    syncEditingModel();
    _.forEach(_.keysIn(ctrl.editingModel.cToAssets), (t: string) => {
      if (!ctrl.editingModel.cToAssets[t]) {
        delete ctrl.editingModel.cToAssets[t];
      }
    });

    ctrl.editingModel.overridableLayoutParameters = [];
    ctrl.editingModel.overrideFields = [];

    if (ctrl.editingModel.dimensionType === 'FLEXIBLE') {
      ctrl.editingModel.width = null;
      ctrl.editingModel.height = null;
    } else if (ctrl.editingModel.dimensionType === 'FIXED_WIDTH') {
      ctrl.editingModel.height = null;
    } else if (ctrl.editingModel.dimensionType === 'FIXED_HEIGHT') {
      ctrl.editingModel.width = null;
    }

    CreativeHelper.doLayoutParams(ctrl);
    if (_.isEmpty(ctrl.editingModel.targeting)) {
      delete ctrl.editingModel.targeting;
    }
    if (ctrl.primaryHtmlAsset) {
      Asset.update(ctrl.editingModel.id, {htmlUrls: ctrl.primaryHtmlAsset.htmlUrls}, ctrl.primaryHtmlAsset.id);
    }

    if (ctrl.isPureMarketplace) {
      ctrl.editingModel.teams = ctrl.editingModel.singularTeam ? [ctrl.editingModel.singularTeam] : null;
    }
  };

  ctrl.createRetailCreative = () => {
    ctrl.editsMade();
    const merchantGuide = ctrl.merchantGuide || {};
    creativeRetailProductModalService.open({
      selRetailProduct: ctrl.selRetailProduct,
      onSelect: (product: any) => {
        const merchantLink = _.get(merchantGuide, [product.merchantId, 'link']);
        ctrl.pending = true;
        return ensureRetailCreativeIsSaved().then((savedCreative: any) => {
          const creativeId = _.get(savedCreative, 'id') || _.get(ctrl.editingModel, 'id') || '';
          return Creative.retailAd(null, creativeId, _.get(ctrl.editingModel, ['layout', 'id']), merchantLink, product.itemId);
        })
          .then(Creative.hydrate)
          .then((creative: any) => {
            applyRetailCreativeUpdate(creative);
            return hydrateSelectedRetailProduct(product.itemId, product.merchantId);
          })
          .finally(() => {
            ctrl.pending = false;
          });
      }
    });
  };

  ctrl.filterAssets = (assets: any[]) => {
    return _.filter(assets || [], (a: any) => a.objectState === 'ACTIVE' && (!a.htmlCreativeType || MediaTypeHelper.isMediaType(a.mimeType, MEDIA_TYPE.html)));
  };

  ctrl.translateMediaTypes = (mimeTypes: any) => {
    return formatMediaTypesTranslate(mimeTypes, (key) => translate.instant(key));
  };

  ctrl.ajsResourceFactory = () => ({
    model: ctrl.editingModel,
    isNew: ctrl.isNew,
    eventHook: ctrl.eventHook,
    selPreview: ctrl.selPreview,
    selPreviewFail: ctrl.selPreviewFail,
    defaultLayoutTypes: ctrl.defaultLayoutTypes,
    allowPreviews: ctrl.allowPreviews,
    selPreviewTrigger: ctrl.selPreviewTrigger
  });
}

export function assetFilter(assets: any[]): any[] {
  return _.filter(assets || [], (a: any) => a.objectState === 'ACTIVE' && (!a.htmlCreativeType || MediaTypeHelper.isMediaType(a.mimeType, MEDIA_TYPE.html)));
}
